import { Fragment, useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { NavLink, useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import Table from '../../Common/components/Table';
import useWhitelabelSettings from './hooks/useWhitelabelSettings';
import { WhitelabelSettingsInfo } from '../lib/types';
import { toast } from 'sonner';
import { Button } from '@/src/shadComponents/ui/button';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
} from '@/src/shadComponents/ui/dialog';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { MoreHorizontal } from 'lucide-react';
import { Menu, Transition } from '@headlessui/react';

const columnHelper = createColumnHelper<WhitelabelSettingsInfo>();

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}

export default function WhitelabelSettings() {
    const [isLoading, setIsLoading] = useState(true);
    const [settings, setSettings] = useState<WhitelabelSettingsInfo[]>([]);
    const [settingToDelete, setSettingToDelete] =
        useState<WhitelabelSettingsInfo | null>(null);
    const { org } = useUserOrg();
    const { getWhitelabelSettingsByOrgId, deleteWhitelabelSettings } =
        useWhitelabelSettings();
    const navigate = useNavigate();

    const columnDefinitions = [
        columnHelper.accessor('name', {
            id: 'name',
            header: () => <span>Name</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('domain', {
            id: 'domain',
            header: () => <span>Domain</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('email_from_name', {
            id: 'email_from_name',
            header: () => <span>Email From Name</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('email_from_address', {
            id: 'email_from_address',
            header: () => <span>Email From Address</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor('updated_at', {
            id: 'updated_at',
            header: () => <span>Last Updated</span>,
            cell: (info) => (
                <span>{new Date(info.getValue()).toLocaleString()}</span>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            header: () => <span></span>,
            cell: (info) => (
                <Menu as="div" className="relative flex-none">
                    <Menu.Button className="-m-2.5 block p-2.5 text-muted-foreground hover:text-foreground">
                        <span className="sr-only">Open options</span>
                        <MoreHorizontal
                            className="h-5 w-5"
                            aria-hidden="true"
                        />
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Menu.Items className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-background py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                            <Menu.Item>
                                {({ active }) => (
                                    <span
                                        onClick={() =>
                                            navigate(
                                                `/org/whitelabel-settings/${info.row.original.id}/edit`,
                                            )
                                        }
                                        className={classNames(
                                            active ? 'bg-muted/50' : '',
                                            'block px-3 py-1 text-sm leading-6 text-foreground',
                                        )}
                                    >
                                        Edit
                                    </span>
                                )}
                            </Menu.Item>
                            <Menu.Item>
                                {({ active }) => (
                                    <span
                                        onClick={() =>
                                            navigate(
                                                `/org/whitelabel-settings/${info.row.original.id}/edit`,
                                            )
                                        }
                                        className={classNames(
                                            active ? 'bg-muted/50' : '',
                                            'block px-3 py-1 text-sm leading-6 text-foreground',
                                        )}
                                    >
                                        View
                                    </span>
                                )}
                            </Menu.Item>
                            <Menu.Item>
                                {({ active }) => (
                                    <span
                                        onClick={() =>
                                            setSettingToDelete(
                                                info.row.original,
                                            )
                                        }
                                        className={classNames(
                                            active ? 'bg-muted/50' : '',
                                            'block px-3 py-1 text-sm leading-6 text-foreground',
                                        )}
                                    >
                                        Delete
                                    </span>
                                )}
                            </Menu.Item>
                        </Menu.Items>
                    </Transition>
                </Menu>
            ),
        }),
    ];

    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            try {
                if (org?.id) {
                    const settingsData = await getWhitelabelSettingsByOrgId(
                        org.id,
                    );
                    if (isMounted) {
                        setSettings(settingsData);
                    }
                }
            } catch (error) {
                console.error('Error fetching whitelabel settings:', error);
                toast.error('Failed to load whitelabel settings');
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org?.id]);

    const handleDelete = async () => {
        if (!settingToDelete) return;

        try {
            await deleteWhitelabelSettings(settingToDelete.id, org?.id);
            setSettings((prev) =>
                prev.filter((setting) => setting.id !== settingToDelete.id),
            );
            toast.success(`Deleted ${settingToDelete.name} successfully`);
            setSettingToDelete(null);
        } catch (error) {
            console.error('Error deleting whitelabel setting:', error);
            toast.error('Failed to delete whitelabel setting');
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="py-4">
            <div className="flex justify-between items-center mb-6 px-6 ">
                <h1 className="text-2xl font-bold tracking-tight text-foreground">Whitelabel Settings</h1>
                <NavLink to="/org/whitelabel-settings/new">
                    <Button>
                        <Plus className="size-4" />
                        Create Whitelabel Settings
                    </Button>
                </NavLink>
            </div>

            {settings.length === 0 ? (
                <div className="text-center p-8 bg-muted/50 rounded-lg">
                    <h3 className="text-base font-semibold mb-2">
                        No whitelabel settings found, domains will default to
                        global settings.
                    </h3>
                    <p className="text-sm leading-6 text-muted-foreground mb-4">
                        Create whitelabel settings to customize email and domain
                        branding for your organization.
                    </p>
                </div>
            ) : (
                <Table data={settings} columns={columnDefinitions} />
            )}

            {/* Delete confirmation dialog */}
            <Dialog
                open={!!settingToDelete}
                onOpenChange={(open) => !open && setSettingToDelete(null)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Deletion</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete "
                            {settingToDelete?.name}"? This action cannot be
                            undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex space-x-2 justify-end">
                        <DialogClose asChild>
                            <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button variant="destructive" onClick={handleDelete}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
